import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import {
  confirmAttendance,
  extractApiError,
  fetchCapacity,
  fetchPreregistrationSummary,
  publicUpdateCounts,
} from '../api.js';
import StripeCheckoutDialog from '../components/StripeCheckoutDialog.jsx';

function formatEur(cents) {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
}

function formatEventDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Page publique de récapitulatif d'une préréservation, accessible via l'UUID
 * (lien partagé par email). Sert aussi de futur écran de paiement par CB :
 * tant que le backend renvoie `online_payment_enabled = false`, on affiche
 * un message « bientôt disponible » à la place du bouton de paiement.
 */
export default function Pay() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // États des dialogs/actions self-service.
  const [editOpen, setEditOpen] = useState(false);
  const [editAdults, setEditAdults] = useState(1);
  const [editChildren, setEditChildren] = useState(0);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [attendSaving, setAttendSaving] = useState(false);
  const [attendError, setAttendError] = useState(null);
  const [stripeOpen, setStripeOpen] = useState(false);
  const [stripeRefreshing, setStripeRefreshing] = useState(false);
  // Message de succès transitoire (Snackbar léger en bandeau, 3s).
  const [successMsg, setSuccessMsg] = useState(null);
  const [bookingsFull, setBookingsFull] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPreregistrationSummary(id)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        setError(
          code === 404
            ? 'Cette préréservation est introuvable. Vérifiez votre lien ou contactez le club.'
            : extractApiError(err, 'Impossible de charger la préréservation.'),
        );
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    fetchCapacity()
      .then((c) => { if (!cancelled) setBookingsFull(Boolean(c?.is_full)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>
        <Typography variant="body2" color="text.secondary">
          Vous pouvez{' '}
          <Link component={RouterLink} to="/retrouver" underline="hover">
            retrouver votre préréservation par email
          </Link>
          .
        </Typography>
      </Container>
    );
  }

  if (!data) return null;

  const isPaid = data.payment_status === 'paid';
  const isPartial = data.payment_status === 'partial';
  const isCancelled = data.payment_status === 'cancelled';
  const isReservation = data.reservation_state === 'reservation';
  const attendanceConfirmed = Boolean(data.attendance_confirmed_at);
  const modificationsClosed = Boolean(data.modifications_closed);

  function openEditDialog() {
    setEditAdults(Number(data.adults) || 1);
    setEditChildren(Number(data.children) || 0);
    setEditError(null);
    setEditOpen(true);
  }

  async function handleAttend() {
    setAttendSaving(true);
    setAttendError(null);
    try {
      const updated = await confirmAttendance(id);
      setData(updated);
      setSuccessMsg('Merci, votre présence est confirmée !');
      setTimeout(() => setSuccessMsg(null), 3500);
    } catch (err) {
      setAttendError(extractApiError(err, 'Impossible de confirmer votre présence.'));
    } finally {
      setAttendSaving(false);
    }
  }

  async function handleStripeSuccess() {
    setStripeOpen(false);
    setStripeRefreshing(true);
    setSuccessMsg('Paiement en cours de validation…');
    try {
      const updated = await fetchPreregistrationSummary(id);
      setData(updated);
      setSuccessMsg('Paiement confirmé — vos bracelets vous seront remis à l’accueil.');
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err) {
      setError(extractApiError(err, 'Paiement validé mais impossible de rafraîchir le récap.'));
    } finally {
      setStripeRefreshing(false);
    }
  }

  async function handleEditSave() {
    setEditSaving(true);
    setEditError(null);
    const adults = Number(editAdults);
    const children = Number(editChildren);
    if (!Number.isFinite(adults) || adults < 1) {
      setEditError('Le nombre d’adultes doit être au moins 1.');
      setEditSaving(false);
      return;
    }
    if (!Number.isFinite(children) || children < 0) {
      setEditError('Le nombre d’enfants doit être positif.');
      setEditSaving(false);
      return;
    }
    try {
      const updated = await publicUpdateCounts(id, { adults, children });
      setData(updated);
      setEditOpen(false);
      setSuccessMsg('Préréservation mise à jour.');
      setTimeout(() => setSuccessMsg(null), 3500);
    } catch (err) {
      setEditError(extractApiError(err, 'Impossible de modifier votre préréservation.'));
    } finally {
      setEditSaving(false);
    }
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: 1, borderColor: 'divider' }}>
        <Stack spacing={3}>
          <Box>
            <Tooltip title="Retour à l'accueil">
              <IconButton
                component={RouterLink}
                to="/"
                size="small"
                aria-label="Retour à l'accueil"
                sx={{ mb: 1, ml: -1 }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1.2 }}>
              {isReservation ? 'Réservation confirmée' : isCancelled ? 'Préréservation annulée' : 'Préréservation'}
            </Typography>
            <Typography variant="h4" sx={{ mt: 0.5 }}>
              Bonjour {data.first_name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, color: 'text.secondary' }}>
              <EventOutlinedIcon fontSize="small" />
              <Typography variant="body2">
                {data.event_name} · {formatEventDate(data.event_date)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              {isReservation && (
                <Chip
                  icon={<EventAvailableOutlinedIcon />}
                  label="Place confirmée"
                  color="primary"
                  size="small"
                />
              )}
              {!isReservation && !isCancelled && (
                <Chip
                  icon={<HourglassEmptyOutlinedIcon />}
                  label="En attente de confirmation"
                  variant="outlined"
                  size="small"
                />
              )}
              {attendanceConfirmed && !isCancelled && (
                <Chip
                  icon={<HowToRegOutlinedIcon />}
                  label="Présence confirmée"
                  color="success"
                  size="small"
                />
              )}
            </Stack>
          </Box>

          {successMsg && <Alert severity="success">{successMsg}</Alert>}

          {isCancelled && (
            <Alert severity="info">
              Cette préréservation a été <strong>annulée</strong>. Si c'est une erreur, contactez le club.
            </Alert>
          )}

          {!isCancelled && !isReservation && (
            <Alert severity="info" icon={<HourglassEmptyOutlinedIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Votre préréservation est enregistrée
              </Typography>
              <Typography variant="body2">
                Elle sera <strong>confirmée par le club</strong> dès qu'un acompte ou le règlement
                aura été reçu. La priorité est donnée à l'ordre d'arrivée des préréservations.
              </Typography>
            </Alert>
          )}

          <Paper
            variant="outlined"
            sx={(theme) => ({
              p: 2,
              // Mode-aware : presque blanc en light, sombre tamise en dark
              // pour que le texte par defaut MUI reste lisible.
              bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : 'action.hover',
            })}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
              <RestaurantOutlinedIcon color="action" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Votre commande
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <Row label="Adultes" value={data.adults} />
              <Row label="Enfants" value={data.children} />
              <Row label="Total repas" value={<strong>{data.meals}</strong>} />
              <Divider sx={{ my: 1 }} />
              <Row label="Montant total" value={<strong>{formatEur(data.due_cents)}</strong>} />
              <Row label="Déjà perçu" value={formatEur(data.paid_cents)} />
              <Row
                label="Solde restant"
                value={
                  <Box
                    component="strong"
                    sx={{
                      color: data.balance_cents > 0 ? 'warning.main' : 'success.main',
                    }}
                  >
                    {formatEur(data.balance_cents)}
                  </Box>
                }
              />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              {isPaid && (
                <Chip icon={<CheckCircleOutlineIcon />} label="Réglé" color="success" />
              )}
              {isPartial && <Chip label="Acompte en cours" color="info" />}
              {data.payment_status === 'unpaid' && !isCancelled && (
                <Chip label="Pas encore réglé" variant="outlined" />
              )}
            </Stack>
          </Paper>

          {isReservation && (
            <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Bracelets à l&apos;accueil
              </Typography>
              <Typography variant="body2">
                À votre arrivée, présentez-vous à la caisse / accueil : un bracelet
                adulte ou enfant vous sera remis pour chaque repas déjà réglé
                (utile notamment pour le dessert). Aucun QR à imprimer.
              </Typography>
            </Alert>
          )}

          {!isCancelled && !isPaid && (
            data.online_payment_enabled ? (
              <Box>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<CreditCardOutlinedIcon />}
                  onClick={() => setStripeOpen(true)}
                  disabled={stripeRefreshing}
                >
                  {stripeRefreshing ? 'Validation…' : `Payer ${formatEur(data.balance_cents)} par CB`}
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  Paiement sécurisé Stripe. Vos bracelets seront remis à l&apos;accueil le jour J.
                </Typography>
              </Box>
            ) : (
              <Alert severity="info" icon={<CreditCardOutlinedIcon />}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Paiement par CB : bientôt disponible
                </Typography>
                <Typography variant="body2">
                  Vous recevrez un email dès l'ouverture du paiement en ligne, ce qui vous évitera
                  de faire la queue à votre arrivée le jour de l'événement. Pour l'instant, le
                  règlement se fera sur place.
                </Typography>
              </Alert>
            )
          )}

          {/* === Section "Gérer ma préréservation" =====================
              Cachée si résa annulée (rien à gérer) ou si on est après la
              deadline (les modifs/annulations passent par le club). */}
          {!isCancelled && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                Gérer ma préréservation
              </Typography>

              {attendError && <Alert severity="error" sx={{ mb: 1.5 }}>{attendError}</Alert>}

              <Stack spacing={1.5}>
                {!attendanceConfirmed && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<HowToRegOutlinedIcon />}
                    onClick={handleAttend}
                    disabled={attendSaving}
                    fullWidth
                  >
                    {attendSaving ? 'Envoi…' : 'Je confirme ma présence'}
                  </Button>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    variant="outlined"
                    startIcon={<EditOutlinedIcon />}
                    onClick={openEditDialog}
                    disabled={modificationsClosed || bookingsFull}
                    fullWidth
                  >
                    Modifier
                  </Button>
                  <Button
                    component={RouterLink}
                    to={`/annuler/${data.id}`}
                    variant="outlined"
                    color="error"
                    startIcon={<CancelOutlinedIcon />}
                    fullWidth
                  >
                    Annuler
                  </Button>
                </Stack>

                {modificationsClosed && (
                  <Alert severity="info" sx={{ mt: 0.5 }}>
                    La date limite de modification est dépassée. Pour ajuster votre
                    préréservation, contactez le club.
                  </Alert>
                )}
                {!modificationsClosed && bookingsFull && (
                  <Alert severity="warning" sx={{ mt: 0.5 }}>
                    <strong>Nous sommes complet.</strong> L'ajout de convives n'est plus
                    possible. Si des places se libèrent, vous pourrez à nouveau augmenter
                    votre réservation.
                  </Alert>
                )}
                {!modificationsClosed && !bookingsFull && (
                  <Typography variant="caption" color="text.secondary">
                    Vous ne pouvez qu'<strong>augmenter</strong> le nombre de convives ici.
                    Pour diminuer, contactez le club.
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          <Box sx={{ textAlign: 'center', pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              N° de préréservation : <code>{data.id}</code>
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* === Dialog "Modifier" ====================================
          Étapeurs simples pour adults / children. Le bouton "Valider"
          appelle PATCH /api/preregistrations/:id (augmentation only). */}
      <Dialog
        open={editOpen}
        onClose={() => !editSaving && setEditOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Modifier ma préréservation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Augmentez le nombre d'adultes ou d'enfants. Pour diminuer, contactez le club
            (le système empêche les baisses depuis cette page).
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Adultes"
              type="number"
              value={editAdults}
              onChange={(e) => setEditAdults(e.target.value)}
              inputProps={{ min: Number(data.adults) || 1, max: 75 }}
              helperText={`Minimum : ${data.adults} (valeur actuelle).`}
              fullWidth
            />
            <TextField
              label="Enfants"
              type="number"
              value={editChildren}
              onChange={(e) => setEditChildren(e.target.value)}
              inputProps={{ min: Number(data.children) || 0, max: 75 }}
              helperText={`Minimum : ${data.children} (valeur actuelle).`}
              fullWidth
            />
            {data.paid_cents > 0 && (
              <Alert severity="info">
                Vous avez déjà réglé {formatEur(data.paid_cents)}. Si vous ajoutez des convives,
                le solde restant à payer augmentera en conséquence.
              </Alert>
            )}
            {editError && <Alert severity="error">{editError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={editSaving || bookingsFull}
          >
            {editSaving ? 'Enregistrement…' : 'Valider'}
          </Button>
        </DialogActions>
      </Dialog>

      <StripeCheckoutDialog
        open={stripeOpen}
        preregistrationId={id}
        amountCents={data.balance_cents}
        onClose={() => setStripeOpen(false)}
        onSuccess={handleStripeSuccess}
      />
    </Container>
  );
}

function Row({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}
